import React from "react";
import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { TenantMergedFeature } from "@/types/tenant-subscription";
import { getCanonicalFeatureTitle } from "@/utils/accessControl";
import {
  formatMergedFeatureValue,
  getMergedFeatureValueVariant,
} from "./utils";

interface TenantMergedFeaturesSectionProps {
  isLoading: boolean;
  mergedFeatures: TenantMergedFeature[];
}

export const TenantMergedFeaturesSection = ({
  isLoading,
  mergedFeatures,
}: TenantMergedFeaturesSectionProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <section className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-zinc-950">
            {t("subscriptions.tenantManagement.features.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("subscriptions.tenantManagement.features.description")}
          </p>
        </div>
        {isLoading ? (
          <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : null}
      </div>

      {mergedFeatures.length === 0 && !isLoading ? (
        <div className="rounded-lg border border-dashed border-border bg-background p-5 text-sm text-muted-foreground">
          {t("subscriptions.tenantManagement.features.empty")}
        </div>
      ) : null}

      <div className="space-y-2">
        {mergedFeatures.map((feature) => (
          <div
            key={feature.key}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-zinc-950">
                {getCanonicalFeatureTitle(feature.key)}
              </p>
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                {feature.type}
              </p>
            </div>
            <Badge
              variant={getMergedFeatureValueVariant(feature)}
              className="min-w-8 justify-center"
            >
              {formatMergedFeatureValue(feature)}
            </Badge>
          </div>
        ))}
      </div>
    </section>
  );
};
